'use client'

import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { BasicImage } from '@/components/page-builder/components/BasicImage'
import { Switch } from '@/components/ui/switch'
import { MONTHLY_VISIBLE_PLANS } from '@/constants/pricing.constants'
import { formatEventName, useAnalytics } from '@/lib/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/lib/analytics/analytics.constants'
import { cn } from '@/lib/styles'
import { PRICING_TEST_IDS } from '@/lib/test-ids'

export function BillingSwitch({
	data,
	yearly,
	setYearly,
	currentTab,
	setCurrentTab,
	tagImage,
	arrowImage,
}: {
	data?: Data.Component<'shared.pricing-plan-detail'>[] | null
	yearly?: boolean
	setYearly?: (checked: boolean) => void
	currentTab: string
	setCurrentTab: (value: string) => void
	tagImage?: Data.Component<'utilities.basic-image'>
	arrowImage?: Data.Component<'utilities.basic-image'>
}) {
	const t = useTranslations('Pricing')
	const { track } = useAnalytics()

	function handleCheckedChange(checked: boolean) {
		const fromBilling = yearly ? 'yearly' : 'monthly'
		const toBilling = checked ? 'yearly' : 'monthly'

		// Track billing cycle change
		track({
			eventType: formatEventName(ANALYTICS_EVENTS.PRICING_BILLING_TOGGLED),
			eventProperties: {
				fromCycle: fromBilling,
				toCycle: toBilling,
			},
		})

		setYearly?.(checked)
		// Keep currentTab valid on mobile
		const allPlans = data ?? []
		const visiblePlans = checked ? allPlans : allPlans.slice(0, MONTHLY_VISIBLE_PLANS)
		if (!visiblePlans.some((plan) => plan?.title === currentTab)) {
			setCurrentTab(visiblePlans[0]?.title ?? '')
		}
	}

	return (
		<div
			className={cn(
				'relative flex justify-center lg:justify-start items-center',
				!yearly && tagImage ? 'lg:justify-between' : 'lg:justify-start',
			)}
			data-test-id={PRICING_TEST_IDS.YEARLY_SWITCH}
		>
			<div className="flex items-center space-x-2">
				<span
					data-text={t('billedMonthly')}
					className={cn(
						'relative inline-block align-middle',
						'after:content-[attr(data-text)] after:block after:h-0 after:overflow-hidden after:invisible after:select-none after:pointer-events-none after:font-bold',
						!yearly
							? 'lok-text-body-bold-medium text-neutral-light-100'
							: 'lok-text-body-regular-medium text-neutral-400',
					)}
				>
					{t('billedMonthly')}
				</span>
				<Switch
					className="data-[state=checked]:bg-brand-salmon"
					onCheckedChange={handleCheckedChange}
					checked={!!yearly}
					data-billing-period={yearly ? t('billedYearly') : t('billedMonthly')}
				/>
				<span
					data-text={t('billedYearly')}
					className={cn(
						'relative inline-block align-middle',
						'after:content-[attr(data-text)] after:block after:h-0 after:overflow-hidden after:invisible after:select-none after:pointer-events-none after:font-bold',
						yearly
							? 'lok-text-body-bold-medium text-neutral-light-100'
							: 'lok-text-body-regular-medium text-neutral-400',
					)}
				>
					{t('billedYearly')}
				</span>
			</div>

			{!yearly && tagImage ? (
				<div className="hidden lg:block relative w-98 h-8.75">
					<BasicImage fill component={tagImage} className="object-contain" />
				</div>
			) : null}

			{!yearly && arrowImage ? (
				<div className="hidden lg:block absolute top-5 right-10">
					<div className="relative w-55.75 h-55">
						<BasicImage fill component={arrowImage} className="object-contain" />
					</div>
				</div>
			) : null}
		</div>
	)
}
